// Copyright 2018-2024 the Deno authors. All rights reserved. MIT license.
// This module is browser compatible.

import { encodeWhitespace } from "../_common/to_file_url.ts";
import { isAbsolute } from "./is_absolute.ts";

/**
 * Converts a path string to a file URL.
 *
 * @example Usage
 * ```ts
 * import { toFileUrl } from "@std/path/windows/to-file-url";
 * import { assertEquals } from "@std/assert";
 *
 * assertEquals(toFileUrl("\\home\\foo"), new URL("file:///home/foo"));
 * assertEquals(toFileUrl("C:\\Users\\foo"), new URL("file:///C:/Users/foo"));
 * assertEquals(toFileUrl("\\\\127.0.0.1\\home\\foo"), new URL("file://127.0.0.1/home/foo"));
 * ```
 * @param path The path to convert.
 * @returns The file URL.
 */
export function toFileUrl(path: string): URL {
  if (!isAbsolute(path)) {
    throw new TypeError(`Path must be absolute: received "${path}"`);
  }
  const [, hostname, pathname] = path.match(
    /^(?:[/\\]{2}([^/\\]+)(?=[/\\](?:[^/\\]|$)))?(.*)/,
  )!;
  const url = new URL("file:///");
  url.pathname = encodeWhitespace(pathname!.replace(/%/g, "%25"));
  if (hostname !== undefined && hostname !== "localhost") {
    url.hostname = hostname;
    if (!url.hostname) {
      throw new TypeError(`Invalid hostname: "${url.hostname}"`);
    }
  }
  return url;
}

// denoCacheMetadata={"headers":{"alt-svc":"h3=\":443\"; ma=2592000,h3-29=\":443\"; ma=2592000","x-goog-hash":"crc32c=AT9A5g==,md5=eAnP3UG76sHQ5TkgPuQ+rg==","x-goog-metageneration":"1","access-control-allow-origin":"*","content-security-policy":"default-src 'none'; script-src 'none'; style-src 'none'; img-src 'none'; font-src 'none'; connect-src 'none'; frame-src 'none'; object-src 'none'; frame-ancestors 'none'; sandbox; form-action 'none';","x-jsr-cache-id":"IAD","x-goog-storage-class":"STANDARD","x-robots-tag":"noindex","accept-ranges":"bytes","expires":"Sat, 08 Nov 2025 10:06:31 GMT","cache-control":"public, max-age=31536000, immutable","x-goog-stored-content-encoding":"identity","via":"1.1 google","content-type":"text/typescript","cross-origin-resource-policy":"cross-origin","last-modified":"Fri, 01 Nov 2024 10:38:03 GMT","x-goog-generation":"1730457483045075","etag":"\"7809cfdd41bbeac1d0e539203ee43eae\"","x-guploader-uploadid":"AHmUCY27n1cOACVer8mFXo8u0ksmrlq7zG2-dl-_7UYSdTPx5acR1MMROhm5wcu4FGam_G0MHOoe_H0Gjw","age":"210","access-control-expose-headers":"*","x-goog-stored-content-length":"1292","server":"UploadServer","date":"Fri, 08 Nov 2024 10:06:31 GMT","content-length":"1292","x-jsr-cache-status":"hit","x-content-type-options":"nosniff"},"url":"https://jsr.io/@std/path/1.0.8/windows/to_file_url.ts","time":1731060601}